import React from "react";
import ReactDOM from "react-dom";
import { ResponsivePie } from "@nivo/pie";
import { patternLinesDef } from "@nivo/core";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import ChartTip from "./ChartTip";
import { tint } from "src/theme/ui";

/**
 * The product's ring, drawn by nivo.
 *
 * It was G2Plot (`@ant-design/charts`), which draws to a canvas and takes one
 * deep options object — so every part of it had to be themed by guessing at
 * nested style keys, its tooltip was an HTML STRING assembled by hand, and
 * several things stayed in the library's own look whatever was passed. nivo
 * renders SVG from React elements: the tooltip is a component, the palette is
 * a function, and the arcs are DOM nodes a click handler can reach.
 *
 * What the ring is, and stays: a slim band on a 3-degree gap with rounded
 * ends, no labels on the arcs, and no entrance animation — an arc that sweeps
 * open shows a share that was never recorded, and it replays on every filter
 * change, which is exactly when a reader is comparing. Hover HIGHLIGHTS by
 * dimming everything else rather than painting a near-black stroke onto the
 * slice under the pointer, which is the one colour these charts are built to
 * avoid.
 */

export type PieDatum = { id: string; value: number };

/**
 * The margin the ring keeps around itself, in px.
 *
 * The hover gesture moves the arc's OUTER radius out by `ACTIVE_OFFSET`, and
 * an SVG clips at its own bounds — so with no margin the arc under the
 * pointer grew straight past the viewBox and came back with its outer edge
 * sliced flat. The band read as broken exactly where the reader was looking.
 *
 * So the growth is paid for in advance: the ring is drawn one pixel smaller
 * than the room it is given, and the hover spends that pixel. Reserving it
 * INSIDE the svg rather than letting the arc paint outside it means no
 * ancestor can clip it either — a dashboard card's body scrolls, and a card
 * that clips is the reason the tooltip is in a portal.
 *
 * It is exported because the ring is not the only thing drawn in that
 * square: the tick scale and the empty track are CSS circles positioned
 * against the same box, and they have to move in by the same amount or the
 * gap between the scale and the band closes up.
 */
export const ACTIVE_OFFSET = 5;
export const RING_MARGIN = ACTIVE_OFFSET + 1;

type Props = {
  data: PieDatum[];
  /** The colour for a slice, by name — shared with the legend beside it. */
  colorOf: (id: string) => string;
  /** Hole size as a fraction of the radius. */
  inner?: number;
  /** Opens a slice, where the card it sits in has somewhere to go. */
  onSelect?: (id: string) => void;
  /** How the tooltip prints the value. */
  format?: (value: number) => string;
  /** Turns the tooltip off, for a ring that is decoration beside its own list. */
  interactive?: boolean;
  /**
   * Which slices are drawn as a hatch rather than as a flat fill.
   *
   * For the arcs that are a KIND rather than a holding — the cash on the
   * account, the brokerage balance. The ring's rule is already that grey is
   * money not invested and a hue is money that is; a hatch says the same
   * thing on a second channel, so it survives greyscale, a colour deficiency
   * and a printed statement, which a difference between two greys does not.
   */
  hatched?: (id: string) => boolean;
  /**
   * The slice the rest of the card is pointing at.
   *
   * The ring dims everything the pointer is NOT on, and that highlight has
   * to work from either end: hovering a row in the legend should raise its
   * arc, and hovering an arc should raise its row. So the state lives in the
   * card and both halves read it.
   */
  active?: string | null;
  onActiveChange?: (id: string | null) => void;
};

/* The pointer says the arc is a control, and only when it is one. */
const Frame = styled.div<{ $clickable?: boolean }>`
  position: absolute;
  inset: 0;

  ${({ $clickable }) => ($clickable ? "cursor: pointer;" : "")}

  /**
   * The hover is a FADE, and only a fade.
   *
   * The dim needs the transition because nivo writes the colour straight
   * onto the path, and without one the whole ring snaps between two palettes
   * as the pointer crosses an arc.
   *
   * What used to be here as well was a scale on path:hover — and a scale is
   * the wrong transform for an arc. An arc is not a box: it
   * is a wedge of a ring, and scaling it moves BOTH of its radii and slides
   * both of its ends along the circumference. So the hovered arc grew past
   * the ring's outer edge, its ends crossed the pad gaps into its
   * neighbours, and where it met the next arc the join came out stepped —
   * which reads as the arc having been cut in two. The growth belongs to the
   * geometry, so it is activeOuterRadiusOffset below: the outer radius moves
   * out, the inner radius and both ends stay exactly where they are.
   */
  path {
    transition: fill 0.18s ease, opacity 0.18s ease;
  }

  /* nivo's own container inherits the page's type rather than its default
     sans, and never takes the pointer where the ring is only a picture. */
  svg text {
    font-family: inherit;
  }
`;

/**
 * The tooltip escapes the card, in a portal.
 *
 * A dashboard card's body scrolls, so it clips — and a bubble that appears
 * beside an arc near the card's edge was being cut in half by it, which is
 * the one thing a tooltip must never be. Rendered into the document and
 * positioned from the pointer, it is bounded by the WINDOW instead.
 *
 * WHERE it goes is the ring's own geometry rather than a fixed offset. Sitting
 * 14px to the right of the cursor, a bubble over the lower-left of the band
 * landed ON the ring: it covered the arc it was describing, and the arc read
 * as broken in two where the white card crossed it. So the offset runs
 * OUTWARD along the radius — from the ring's centre through the pointer and
 * past it — which is the one direction that is guaranteed to be off the
 * chart, whichever arc is under the pointer. The corner it hangs from is
 * picked from the same vector, and the window's edges still win.
 */
const TipLayer = styled.div<{
  $x: number;
  $y: number;
  $ax: number;
  $ay: number;
}>`
  position: fixed;
  top: ${({ $y }) => $y}px;
  left: ${({ $x }) => $x}px;
  z-index: 1100;
  /* The bubble hangs off whichever corner faces the ring, so it grows away
     from the chart rather than over it. */
  transform: translate(${({ $ax }) => $ax}%, ${({ $ay }) => $ay}%);
  pointer-events: none;
`;

const PieRing: React.FC<Props> = ({
  data,
  colorOf,
  inner = 0.85,
  onSelect,
  format = (value) => String(value),
  interactive = true,
  hatched,
  active: activeFromCard,
  onActiveChange,
}) => {
  /* Which arc the pointer is on, so the others can step back. Held here
     rather than left to the library — its own "active" state paints a
     stroke, and what this system does instead is dim — unless the card is
     holding it, in which case the ring follows the card and the card follows
     the legend too. */
  const [ownActive, setOwnActive] = React.useState<string | null>(null);
  const active = activeFromCard !== undefined ? activeFromCard : ownActive;
  const setActive = React.useCallback(
    (id: string | null) => {
      setOwnActive(id);
      onActiveChange?.(id);
    },
    [onActiveChange],
  );

  /**
   * False on the first paint, true from the second.
   *
   * A layout effect rather than a plain one so the flag flips before the
   * browser has painted anything a spring could animate from — with a
   * passive effect the first render can commit, and nivo then springs the
   * arcs open from nothing, which is the entrance this is written to avoid.
   */
  const [moving, setMoving] = React.useState(false);
  React.useLayoutEffect(() => setMoving(true), []);

  /* Where the pointer is, for the bubble that follows it out of the card. */
  const [at, setAt] = React.useState<{
    x: number;
    y: number;
    ax: number;
    ay: number;
  } | null>(null);

  const frame = React.useRef<HTMLDivElement>(null);

  /**
   * The pointer, pushed outward along the ring's radius.
   *
   * `ax`/`ay` are the percentages the bubble is translated by, so the corner
   * nearest the ring is the one it hangs from: fully off to the left when the
   * pointer is on the left of the band, centred when it is near the vertical
   * axis, and so on. A bubble that would leave the window is pulled back to
   * the other side.
   */
  const place = React.useCallback(
    (event: { clientX: number; clientY: number }) => {
      const box = frame.current?.getBoundingClientRect();
      const cx = box ? box.left + box.width / 2 : event.clientX;
      const cy = box ? box.top + box.height / 2 : event.clientY;

      let dx = event.clientX - cx;
      let dy = event.clientY - cy;
      const length = Math.hypot(dx, dy) || 1;
      dx /= length;
      dy /= length;

      /* Clear of the band by the arc's own corner radius plus a hair. */
      const gap = 12;
      const x = event.clientX + dx * gap;
      const y = event.clientY + dy * gap;

      /* 0.35 is the dead zone: within it the bubble centres on that axis
       instead of committing to a side, which is what keeps a tooltip on the
       ring's north or south point from jumping left and right. */
      let ax = dx > 0.35 ? 0 : dx < -0.35 ? -100 : -50;
      let ay = dy > 0.35 ? 0 : dy < -0.35 ? -100 : -50;

      /* The window has the last word: 240px covers the longest name this
       draws, and 56px the bubble's own height. */
      if (ax === 0 && x + 240 > window.innerWidth) ax = -100;
      if (ax === -100 && x - 240 < 0) ax = 0;
      if (ay === -100 && y - 56 < 0) ay = 0;
      if (ay === 0 && y + 56 > window.innerHeight) ay = -100;

      setAt({ x, y, ax, ay });
    },
    [],
  );

  /**
   * An arc's fill, dimmed while the pointer is on a different one.
   *
   * The dim used to be `withAlpha(own, 0.3)` — a translucent fill — and that
   * broke the hatched arcs outright. A hatch is an SVG `<pattern>` whose own
   * background is declared `inherit` so it takes the slice's colour; hand it
   * an `rgba()` instead of a hex and the inheritance does not resolve, and
   * the pattern's ground fell through to black. So on hover the cash arc, a
   * pale grey hatch, turned into a near-black blob.
   *
   * `tint` blends toward white and returns a solid hex. Over a white card it
   * looks the same as 30% alpha did, it composites the same on any other
   * ground, and a pattern can inherit it.
   */
  const paint = React.useCallback(
    (id: string) => {
      const own = colorOf(id);
      return active && active !== id ? tint(own, 0.7) : own;
    },
    [active, colorOf],
  );

  return (
    <Frame ref={frame} $clickable={Boolean(onSelect)}>
      <ResponsivePie
        data={data}
        /* Room for the hover to grow into — see RING_MARGIN above. */
        margin={{
          top: RING_MARGIN,
          right: RING_MARGIN,
          bottom: RING_MARGIN,
          left: RING_MARGIN,
        }}
        innerRadius={inner}
        /* The gap between arcs, in degrees. It was a 3px white stroke, which
           is the same reading — a band broken into segments rather than one
           unbroken ring — without a stroke colour that has to be told what
           the card behind it is. */
        /* 3.5 rather than 2.5: on a thin band a 2.5-degree gap closes up —
           the rounded ends of two neighbouring arcs nearly touch — and the
           ring reads as one continuous line with kinks in it rather than as
           separate quantities. */
        padAngle={3.5}
        cornerRadius={8}
        borderWidth={0}
        colors={({ id }) => paint(String(id))}
        /* The hatch is defined once and matched by name, so a slice's
           texture travels with what it IS rather than with where it lands in
           the rotation. */
        defs={[
          patternLinesDef("hatch", {
            /**
             * A light ground with the arc's OWN colour in the lines, the
             * other way round from how this started.
             *
             * It was the arc's colour as the ground with white lines, which
             * made a hatched arc read as a solid block of colour that
             * something had been scratched off — the texture was the absence
             * and the colour was the field. Inverted, the texture is the
             * mark: the arc still carries its own hue, because nivo resolves
             * inherit for the line colour as readily as for the ground
             * (per-datum patterns named hatch.fg.<hex>), and the ground is one
             * step off the card so the band's shape stays legible without
             * competing with the arcs beside it.
             *
             * 1.5 on 3: the line and the gap are the same width — nivo's
             * spacing is the PERIOD, so an equal pair is always half of it —
             * and finer than the 2 on 4 it replaces, which at a 13px band
             * read as stripes rather than as a texture. Three strokes
             * across a band is a texture; six is a shading.
             *
             * The ground goes one step darker with them, to the 100. A hatch
             * this fine leaves more ground than line, so at the 50 the arc
             * was nearly the colour of the card behind it and its shape went
             * soft; the 100 still sits well below the lines, which carry the
             * arc's own hue, so the mark stays the mark and the ground stays
             * the ground.
             */
            background: colors.gray[100],
            color: "inherit",
            rotation: -45,
            lineWidth: 1.5,
            spacing: 3,
          }),
        ]}
        fill={
          hatched
            ? data
                .filter((datum) => hatched(String(datum.id)))
                .map((datum) => ({ match: { id: datum.id }, id: "hatch" }))
            : []
        }
        /* No labels on the arcs at all. A figure printed on a coloured
           field has to take a colour that reads on it, and this palette has
           no single answer — so the share is printed once, in the legend's
           own column. What was here (a white chip carrying the share) is in
           the history of this file if it is ever wanted back. */
        enableArcLabels={false}
        enableArcLinkLabels={false}
        isInteractive={interactive}
        /* Controlled, because the card and its legend share this state: a
           legend row and the arc it names light up together. */
        activeId={active}
        onActiveIdChange={(id) => setActive(id === null ? null : String(id))}
        /* The whole hover gesture: the outer edge steps out 5px and nothing
           else about the arc changes. */
        activeOuterRadiusOffset={ACTIVE_OFFSET}
        activeInnerRadiusOffset={0}
        /**
         * No entrance, but every CHANGE moves.
         *
         * It was flatly false, for a good reason that was stated too
         * broadly: an arc that sweeps open on load shows a share that was
         * never recorded, and it replays on every filter change — which is
         * exactly when a reader is comparing. That argument is about the
         * FIRST paint. It says nothing about what should happen when the
         * data genuinely changes under a reader who just asked it to, which
         * is the one moment a transition earns its place: drilling into a
         * group, the arcs travel from the composition you clicked to the
         * holdings inside it, so the new ring is visibly made of the old one
         * rather than replacing it.
         *
         * So it is false for one frame and true after: the ring still draws
         * itself instantly on arrival, and moves from then on.
         */
        animate={moving}
        motionConfig="stiff"
        onMouseEnter={(datum, event) => {
          setActive(String(datum.id));
          place(event);
        }}
        onMouseMove={(_datum, event) => place(event)}
        onMouseLeave={() => {
          setActive(null);
          setAt(null);
        }}
        onClick={onSelect ? (datum) => onSelect(String(datum.id)) : undefined}
        /* The library's own bubble is suppressed — it renders inside the
           chart, which is inside the card that clips it. Ours is the portal
           below, drawn from the same datum. */
        tooltip={() => <span />}
      />

      {interactive && active && at
        ? ReactDOM.createPortal(
            <TipLayer $x={at.x} $y={at.y} $ax={at.ax} $ay={at.ay}>
              <ChartTip
                color={colorOf(active)}
                name={active}
                value={format(
                  Number(data.find((datum) => datum.id === active)?.value ?? 0),
                )}
              />
            </TipLayer>,
            document.body,
          )
        : null}
    </Frame>
  );
};

export default PieRing;
